import { useClickThrottle } from "hooks/useClickThrottle";
import React, { ReactNode, useContext, useEffect, useState } from "react";
import Styled from 'styled-components'

const computeValue = (value: number | string) => {
    if(typeof value === "number") {
        return `${value || 0}px`;
    }
    return value;
}

type BoxStylePropsType = {
    height?: number | string;
    width?: number | string;
    border?: string;
    borderRadius?: number | string;
    backgroundColor?: string;
    padding?: number | string;
    margin?: number | string;
    transition?: string | boolean;
    transform?: string;
    transformOrigin?: string;
    maxHeight?: number | string;
    maxWidth?: number | string;
    relative?: boolean;
    scroll?: boolean;
    shadow?: boolean;
    overflow?: string;
    hoverBackgroundColor?: string;
    fullWidth?: boolean;
    round?: true;
    flex?: number;
    boxShadow?: string;
    contentBox?: boolean;
    noBorderBottom?: boolean;
    background?: string;
    borderTop?:string;
    borderBottom?: string;
    borderRight?: string;
    borderLeft?: string;
    minHeight?: number | string;
    minWidth?: number | string;
    noOutline?: boolean;
    focusBackgroundColor?: string;
    opacity?: string;
}

// Define interfaces for props
export type BoxPropsType = BoxStylePropsType & {
    onClick?: (event: any) => void;
    onDoubleClick?: (event: any) => void;
    onMouseOut?: (event: any) => void;
    onMouseOver?: (event: any) => void;
    onMouseEnter?: (event: any) => void;
    onMouseLeave?: (event: any) => void;
    className?: string;
    hover?: BoxStylePropsType;
    active?: BoxStylePropsType;
    timeout?: number;
    id?: string;
    noPrint?: boolean;
    zIndex?: number;
    ref?: any;
    tabIndex?: number;
    onKeyDown?: (event: KeyboardEvent) => void;
    focus?: BoxStylePropsType;
    dataTestId?: string;
    display?: string;
    onMouseDown?: (event: any) => void;
    onMouseUp?: (event: any) => void;
    customScrollbar?: boolean;
    children?: ReactNode
}

const getStyle = (props: BoxPropsType) => {
    return `
    ${props.width && `width: ${computeValue(props.width)}`};
    ${props.height && `height: ${computeValue(props.height)}`};
    ${props.margin && `margin: ${computeValue(props.margin)}`};
    ${props.padding && `padding: ${computeValue(props.padding)}`};
    ${props.borderRadius && `border-radius: ${computeValue(props.borderRadius)}`};
    ${props.border && `border: ${computeValue(props.border)}`};
    ${props.backgroundColor && `background-color: ${props.backgroundColor}`};
    ${props.background && `background: ${props.background}`};
    ${props.relative && `position: relative;`};
    ${props.shadow && `box-shadow: 0px 0px 8px 0px rgba(0, 0, 0, 0.15);`};
    ${props.fullWidth && `width: 100%;`};
    ${props.round && `border-radius: 50%;`};
    ${props.transition && `transition : ${typeof props.transition === "string" ? props.transition : "all 0.5s"};`};
    ${props.transform && `transform : ${props.transform};`};
    ${props.transformOrigin && `transform-origin : ${props.transformOrigin};`};
    ${props.maxHeight && `max-height : ${computeValue(props.maxHeight)};`};
    ${props.maxWidth && `max-width : ${computeValue(props.maxWidth)};`};
    ${props.scroll && `overflow: auto;`};
    ${props.overflow && `overflow: ${props?.overflow};`};
    ${props.onClick && `cursor: pointer;`};
    ${props.flex && `flex: ${props.flex};`};
    ${props.boxShadow && `box-shadow: ${props.boxShadow};`};
    ${props.contentBox && `box-sizing: content-box;`};
    ${props.borderBottom && `border-bottom: ${computeValue(props.borderBottom)};`};
    ${props.borderTop && `border-top: ${computeValue(props.borderTop)};`};
    ${props.borderLeft && `border-left: ${computeValue(props.borderLeft)};`};
    ${props.borderRight && `border-right: ${computeValue(props.borderRight)};`};
    ${props.noBorderBottom && `border-bottom: none;`};
    ${props?.zIndex && `z-index: ${props.zIndex};`};
    ${props?.minHeight && `min-height: ${computeValue(props.minHeight)};`};
    ${props?.minWidth && `min-width: ${computeValue(props.minWidth)};`};
    ${props?.noOutline && `outline: none;`};
    ${props?.display && `display: ${props.display};`};
    ${props.borderTop && `border-top: ${computeValue(props.borderTop)};`};
    ${props.transformOrigin && `transform-origin: ${props?.transformOrigin};`};
    ${props?.opacity && `opacity: ${props.opacity};`};
`
}

const ContainerDiv = Styled.div<BoxPropsType>`
    box-sizing: border-box;
    width: max-content;
    height: initial;
    margin: 0;
    padding: 0;
    border-radius: 0;
    border: 0;
    background-color: transparent;
    ${props => getStyle(props)}
    :hover {
        ${props => props.hoverBackgroundColor && `
        background-color: ${props.hoverBackgroundColor};
    `};
    :hover {
        ${props => getStyle(props?.hover || {})}
    };
    :active {
        ${props => getStyle(props?.active || {})}
    };
    ${props => props?.customScrollbar && `
        ::-webkit-scrollbar {
            width: 4px;
        }

        /* Track */
            ::-webkit-scrollbar-track {
            background: transparent;
        }

        /* Handle */
            ::-webkit-scrollbar-thumb {
            border-radius: 8px;
            background: #CACAD8;
            height: 60px;
            padding-top: 8px;
            padding-right: 8px;
        }

        /* Handle on hover */
            ::-webkit-scrollbar-thumb:hover {
            background: #555;
        }
    `}

    @keyframes opacityAppearMoveUp {
        0% {
            transform: translateY(60px);
            opacity: 0;
        }

        100% {
            transform: translateY(0px);
            opacity: 1;
        }
    }
    
    @keyframes opacityAppearMoveLeft {
        0% {
            transform: translateX(60px);
            opacity: 0;
        }

        100% {
            transform: translateX(0px);
            opacity: 1;
        }
    }
    
    @keyframes opacityAppear {
        0% {
            opacity: 0;
        }

        100% {
            opacity: 1;
        }
    }
`;

const Box: React.FC<BoxPropsType> = ({ children, className, hover, active, onClick, onDoubleClick, timeout, id = "", noPrint = false, ref = null, dataTestId, ...props }) => {
    const throttledOnClick = useClickThrottle(onClick, timeout);

    return (
        <ContainerDiv data-test-id={dataTestId} id={id} ref={ref} onDoubleClick={onDoubleClick} onClick={throttledOnClick as any} className={`sui-box ${className || ''}${noPrint ? ' no-print' : ''}`} {...props} hover={hover} active={active}>{children}</ContainerDiv>
    )
};


export default Box;